import { useEffect, useState } from "react";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchSessions, type ConferenceSession } from "./api";
import { formatDateTime, pickLocalized } from "./format";

export function SchedulePage() {
  const { t, locale } = useLanguage();
  const [sessions, setSessions] = useState<ConferenceSession[]>([]);
  const [watch, setWatch] = useState<ConferenceSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    fetchSessions()
      .then(setSessions)
      .catch((e) => setError(e instanceof Error ? e.message : "error"));

  useEffect(() => {
    load();
    const id = window.setInterval(load, 15000);
    return () => window.clearInterval(id);
  }, []);

  const openStream = (s: ConferenceSession) => {
    if (s.stream_provider === "demo" || !s.stream_url) {
      setWatch(s);
      return;
    }
    if (s.stream_provider === "youtube" && s.stream_url.includes("youtube")) {
      setWatch(s);
      return;
    }
    window.open(s.stream_url, "_blank", "noopener,noreferrer");
  };

  const youtubeEmbed = (url: string | null) => {
    if (!url) return null;
    const m = url.match(/(?:v=|youtu\.be\/)([\w-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  };

  return (
    <div>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.schedule.title}
      </h1>
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      <div className="dn-grid">
        {sessions.map((s) => (
          <article key={s.id} className="dn-card">
            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
              <h3>{pickLocalized(s, locale, "title_en", "title_ar")}</h3>
              {s.status === "live" && <span className="dn-badge-live">{t.schedule.liveNow}</span>}
            </div>
            <p className="dn-muted">
              {formatDateTime(s.starts_at, locale)} – {formatDateTime(s.ends_at, locale)}
            </p>
            {(s.speaker_en || s.speaker_ar) && (
              <p>{pickLocalized(s, locale, "speaker_en", "speaker_ar")}</p>
            )}
            {(s.venue_en || s.venue_ar) && (
              <p className="dn-muted">{pickLocalized(s, locale, "venue_en", "venue_ar")}</p>
            )}
            {s.status === "scheduled" && <p className="dn-muted">{t.schedule.upcoming}</p>}
            {s.status === "ended" && <p className="dn-muted">{t.schedule.ended}</p>}
            {s.status === "live" && (
              <button type="button" className="dn-btn dn-btn-primary" onClick={() => openStream(s)}>
                {t.schedule.watchLive}
              </button>
            )}
          </article>
        ))}
      </div>

      {watch && (
        <div className="dn-modal-backdrop" role="dialog" aria-modal="true">
          <div className="dn-modal">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <h3>{pickLocalized(watch, locale, "title_en", "title_ar")}</h3>
              <button type="button" className="dn-btn dn-btn-ghost" onClick={() => setWatch(null)}>
                {t.common.close}
              </button>
            </div>
            {watch.stream_provider === "youtube" && youtubeEmbed(watch.stream_url) ? (
              <iframe
                title={t.schedule.liveStreamTitle}
                src={youtubeEmbed(watch.stream_url) || undefined}
                style={{ width: "100%", aspectRatio: "16/9", border: 0, borderRadius: 12 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="dn-demo-player">
                <div>
                  <div className="dn-badge-live" style={{ marginBottom: "0.75rem" }}>
                    {t.schedule.liveNow}
                  </div>
                  <h3>{t.schedule.demoTitle}</h3>
                  <p>{t.schedule.demoBody}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
